import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUPABASE_URL } from '@/integrations/supabase/client';

/**
 * Customer-facing unsubscribe page at /r/:token/unsubscribe, linked from the
 * footer of every review request email. One tap stops review requests from
 * this business for good (customers.review_requests_opted_out). No login.
 * Preview tokens render the page but record nothing.
 */

interface PageData {
  preview: boolean;
  brand_name: string;
  logo_url: string | null;
  primary_color: string | null;
}

type Phase = 'loading' | 'invalid' | 'confirm' | 'sending' | 'done' | 'failed';

export default function ReviewUnsubscribe() {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<PageData | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');

  useEffect(() => {
    document.title = 'Unsubscribe';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/review-page?token=${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error('invalid');
        const page = (await res.json()) as PageData;
        if (!cancelled) { setData(page); setPhase('confirm'); }
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const confirm = async () => {
    setPhase('sending');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/review-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, unsubscribe: true }),
      });
      if (!res.ok) throw new Error('failed');
      setPhase('done');
    } catch {
      setPhase('failed');
    }
  };

  const accent = data?.primary_color || '#1e3a8a';

  if (phase === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </main>
    );
  }

  if (phase === 'invalid' || !data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-sm text-center">
          <h1 className="text-xl font-semibold">This link is not valid</h1>
          <p className="mt-2 text-sm text-muted-foreground">It may have been typed incorrectly. Please use the link from your email.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      {data.preview && (
        <div className="mb-6 rounded-md border border-warning/40 bg-warning/5 px-3 py-1.5 text-xs text-muted-foreground">
          Preview. Nothing you tap here is recorded.
        </div>
      )}
      <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
        {data.logo_url ? (
          <img src={data.logo_url} alt={data.brand_name} className="mx-auto mb-5 max-h-16 max-w-[200px] object-contain" />
        ) : (
          <div className="mb-5 text-lg font-semibold" style={{ color: accent }}>{data.brand_name}</div>
        )}

        {phase === 'done' ? (
          <>
            <h1 className="text-lg font-semibold">You're unsubscribed</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {data.brand_name} won't send you any more review requests. You can close this page.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold">Stop review requests?</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {data.brand_name} will no longer ask you to review their work. Quotes, invoices and job updates are not affected.
            </p>
            {phase === 'failed' && (
              <p className="mt-3 text-sm text-destructive">That didn't go through. Please try again.</p>
            )}
            <Button className="mt-6 w-full" style={{ backgroundColor: accent }} disabled={phase === 'sending'} onClick={confirm}>
              {phase === 'sending' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Unsubscribe
            </Button>
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Powered by Steddy</p>
    </main>
  );
}
